import { makeStyles } from '@material-ui/styles';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { CoverPhoto as CoverPhotoIcon, TipsBasic, PictureSplit } from 'common/components/svgs';
import Button from 'common/components/button';
import Dragger from 'common/components/dragger';
import hexToRGBA from 'common/utils/hex-to-rgba';
import Progress from 'common/components/progress';
import theme from 'common/theme';
import Typography from 'common/components/typography';
import { getHeader } from '~helpers';
import { pmsEndpoints } from '~settings/endpoints';
import event from '~helpers/mixpanel';
import OnboardingContext from '../context';
import { pageType } from '~constants/pages';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const IMAGE_MAX_SIZE = 20 * 1024 ** 2; //20mb
const IMAGE_MIN_WIDTH = 600;
const IMAGE_MIN_HEIGHT = 375;

const CoverPhoto = () => {
  const { t } = useTranslation();
  const onboardingData = useContext(OnboardingContext);
  const [previewFile, setPreviewFile] = useState(onboardingData?.coverPhoto?.file);
  const [progress, setProgress] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const previewImageRef = useRef();

  const useStyles = makeStyles(theme => {
    return {
      coverPhotoWrapper: {
        margin: 80,
        padding: '72px 0 100px',
      },
      title: {
        textAlign: 'center',
        color: theme.colors.gray10,
        margin: '0 0 48px !important',
        lineHeight: '32px',
        fontWeight: 600,
      },
      draggerWrapper: {
        position: 'relative',
        margin: '0 auto 32px',
        width: 565,
        background: theme.colors.gray1,
        padding: '40px 40px',
        boxShadow: '0px 12px 32px 2px rgba(107, 146, 243, 0.19)',
        borderRadius: 2,
      },
      coverPhoto: {
        display: 'block',
        height: 52,
        width: 52,
        margin: '0 auto 30px',
      },
      dragger: {
        '& > .ant-upload-drag': {
          height: '324px',
          background: hexToRGBA(theme.colors.blue1, 0.6),
          border: previewFile ? 'none' : `2px dashed ${theme.colors.gray5}`,
          position: 'relative',
          overflow: 'hidden',
        },
      },
      draggerText: {
        lineHeight: '22px',
      },
      list: {
        margin: '32px 0 0',
        paddingLeft: 14,
      },
      previewImage: {
        position: 'absolute',
        height: '100%',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      progressCover: {
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        top: 0,
        height: '100%',
        width: '100%',
        zIndex: 10,
        background: hexToRGBA(theme.colors.gray10, 0.4),
      },
      progress: {
        width: '65%',
      },
      tipsWrapper: {
        position: 'absolute',
        left: '100%',
        top: 0,
        marginLeft: 24,
        padding: '24px 16px',
        background: hexToRGBA(theme.colors.blue6, 0.06),
        width: 266,
      },
      tipsIcon: {
        height: 20,
        width: 20,
        fill: theme.colors.mainGreen6,
        marginRight: 8,
      },
      tipsTitle: {
        display: 'flex',
        marginBottom: 10,
      },
      tipsList: {
        margin: 0,
        paddingLeft: 24,
      },
      changePhotoBtn: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 10,
        padding: '2px 6px',
      },
      uploadFailed: {
        display: 'block',
        margin: '10px 0 16px',
      },
      pictureSplit: {
        height: 42,
        width: 42,
      },
    };
  });

  const classes = useStyles();

  useEffect(() => {
    event('Viewed OP Cover Photo Page', { 'Current Page Type': pageType.onBoardingPage });
  }, []);

  useEffect(() => {
    if (previewFile && FileReader && previewImageRef) {
      const fr = new FileReader();
      fr.onload = () => {
        previewImageRef.current.src = fr.result;
      };
      fr.readAsDataURL(previewFile);
    }
  }, [previewFile, previewImageRef]);

  const uploadContentList = [
    t('landlord.onboarding.cover_photo.upload_content_max_20_mb'),
    t('landlord.onboarding.cover_photo.upload_content_for_best_effect'),
    t('landlord.onboarding.cover_photo.upload_content_high_definition'),
  ];

  const tipsList = [
    t('landlord.onboarding.cover_photo.tips_more_potential_tenants'),
    t('landlord.onboarding.cover_photo.tips_can_always_edit_or_add'),
    t('landlord.onboarding.cover_photo.tips_shoot_inward'),
  ];

  const handleUploadPropertyImage = (file, setProgress) => {
    const data = new FormData();
    data.append('document', file);
    data.append('property_id', decodeBase64(onboardingData?.propertyId).id);
    data.append('category', 'general');

    const config = {
      onUploadProgress: progressEvent => {
        const progress = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(progress);
      },
      headers: getHeader(),
      timeout: 600 * 1000,
    };

    event('Loaded Cover Photo on Onboarding', {
      'Property Type': onboardingData.propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });

    axios
      .post(pmsEndpoints.uploadPropertyImage.url(), data, config)
      .then(response => {
        if (!response.data) {
          throw new Error('unknown error.');
        }
        setProgress(100);

        setTimeout(() => {
          setProgress(null);
          setUploadSuccess(true);
          onboardingData?.setCoverPhoto({ ...response.data, file });
        }, 300);
      })
      .catch(() => {
        setProgress(null);
        setUploadSuccess(false);
      });
  };

  const getImageSize = file => {
    return new Promise(resolve => {
      const img = new Image();
      const fr = new FileReader();
      fr.onload = () => {
        img.onload = function () {
          resolve({ width: img.width, height: img.height }); // image is loaded; sizes are available
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(file);
    }).then(size => {
      return size;
    });
  };

  const handleCustomUpload = async args => {
    const fileSize = await getImageSize(args.file);
    if (fileSize.width < IMAGE_MIN_WIDTH || fileSize.height < IMAGE_MIN_HEIGHT) {
      message.error(t('landlord.onboarding.cover_photo.width_and_height'));
      return false;
    }

    if (args.file.size > IMAGE_MAX_SIZE) {
      message.error(t('landlord.onboarding.cover_photo.size_limit'));
      return false;
    }
    setPreviewFile(args.file);
    setProgress(0);
    handleUploadPropertyImage(args.file, setProgress);
  };

  return (
    <div className={classes.coverPhotoWrapper}>
      <Typography
        textType="title"
        className={classes.title}
        fontSize={24}
        lineHeight={32}
        level={2}
      >
        {t('landlord.onboarding.cover_photo.title')}
      </Typography>

      <div className={classes.draggerWrapper}>
        <Dragger
          accept={'.png,.jpg'}
          className={classes.dragger}
          maxCount={1}
          disabled={progress !== null}
          showUploadList={false}
          customRequest={handleCustomUpload}
        >
          <div
            onClick={() => {
              event('Clicked OP Cover Photo Page', {
                'Clicked Position': 'Photo Area',
                'Property Type': onboardingData.propertyTypeSlug,
                'Current Page Type': pageType.onBoardingPage,
              });
            }}
          >
            <Choose>
              <When condition={previewFile}>
                <If condition={progress !== null}>
                  <div className={classes.progressCover}>
                    <Progress
                      strokeColor={theme.colors.mainGreen6}
                      percent={progress}
                      showInfo={false}
                      className={classes.progress}
                    />
                  </div>
                </If>
                <If condition={progress === null && uploadSuccess}>
                  <Button type="primary" className={classes.changePhotoBtn}>
                    <Typography
                      fontSize={12}
                      lineHeight={22}
                      textType="text"
                      themeColor={theme.colors.gray1}
                    >
                      {t('landlord.onboarding.cover_photo.change_photo')}
                    </Typography>
                  </Button>
                </If>
                <If condition={progress === null && uploadSuccess === false}>
                  <div className={classes.progressCover}>
                    <PictureSplit className={classes.pictureSplit} />
                    <Typography
                      fontSize={12}
                      lineHeight={22}
                      textType="text"
                      themeColor={theme.colors.gray1}
                      className={classes.uploadFailed}
                    >
                      {t('landlord.onboarding.cover_photo.upload_failed')}
                    </Typography>
                    <Button type="primary" className={classes.reloadBtn}>
                      <Typography
                        fontSize={12}
                        lineHeight={22}
                        textType="text"
                        themeColor={theme.colors.gray1}
                      >
                        {t('landlord.onboarding.cover_photo.reload')}
                      </Typography>
                    </Button>
                  </div>
                </If>
                <img ref={previewImageRef} className={classes.previewImage} />
              </When>
              <Otherwise>
                <CoverPhotoIcon className={classes.coverPhoto} />
                <Typography fontSize={12} textType="text" className={classes.draggerText}>
                  {t('landlord.onboarding.cover_photo.drag_and_drop_an_image')}
                </Typography>
              </Otherwise>
            </Choose>
          </div>
        </Dragger>

        <ul className={classes.list}>
          {uploadContentList.map((item, index) => (
            <li key={index}>
              <Typography
                fontSize={12}
                themeColor={hexToRGBA(theme.colors.gray10, 0.65)}
                textType="text"
              >
                {item}
              </Typography>
            </li>
          ))}
        </ul>

        <div className={classes.tipsWrapper}>
          <Typography
            fontSize={14}
            themeColor={hexToRGBA(theme.colors.gray10, 0.65)}
            textType="text"
            lineHeight={22}
            className={classes.tipsTitle}
          >
            <TipsBasic className={classes.tipsIcon} />
            {t('landlord.onboarding.cover_photo.tips')}
          </Typography>
          <ul className={classes.tipsList}>
            {tipsList.map((item, index) => (
              <li key={index}>
                <Typography
                  fontSize={12}
                  themeColor={hexToRGBA(theme.colors.gray10, 0.65)}
                  textType="text"
                >
                  {item}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CoverPhoto;
